import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * BubbleGroup — a row of pill toggles for a single choice.
 *
 *   <BubbleGroup
 *     value={mode}
 *     onValueChange={setMode}
 *     options={[
 *       { value: 'register', label: 'Register a new domain' },
 *       { value: 'existing', label: 'Customer has a domain' },
 *     ]}
 *   />
 *
 * The pattern existed as a hand-rolled `<button>` row on SalesAddCustomerPage
 * (domain mode) before the 2026-09-11 product picker needed it three more times
 * on the same page. Four copies of a control is how a design system rots, so it
 * became a primitive instead (CLAUDE.md §4.2 — the shared component is the
 * default; a local copy is the defect).
 *
 * Why buttons and not `Radio`: these are chunky, thumb-sized, scanned-at-a-glance
 * choices an agent hits during a live phone call, not a form field in a column.
 * Semantics are preserved with radiogroup/radio roles + arrow-key navigation, so
 * it is a radio group to a screen reader and a row of pills to everyone else.
 *
 * Accessibility: roving tabindex (only the selected bubble is tabbable),
 * Arrow/Home/End move AND select, matching the WAI-ARIA radio group pattern.
 * Every bubble carries the 44px minimum touch target.
 */

export interface BubbleOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  /** Small muted line under the label — a price, a hint. */
  hint?: React.ReactNode;
  disabled?: boolean;
}

export interface BubbleGroupProps<T extends string = string> {
  value: T | null;
  onValueChange: (value: T) => void;
  options: ReadonlyArray<BubbleOption<T>>;
  /** Disables every bubble — e.g. while a form is submitting. */
  disabled?: boolean;
  /** Accessible name for the group. Falls back to aria-labelledby. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
}

export function BubbleGroup<T extends string = string>({
  value,
  onValueChange,
  options,
  disabled = false,
  className,
  ...aria
}: BubbleGroupProps<T>) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const enabled = options
    .map((o, i) => ({ o, i }))
    .filter(({ o }) => !o.disabled && !disabled);

  function move(from: number, delta: number) {
    if (enabled.length === 0) return;
    const pos = enabled.findIndex(({ i }) => i === from);
    const next = enabled[(pos + delta + enabled.length) % enabled.length]!;
    onValueChange(next.o.value);
    refs.current[next.i]?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent, index: number) {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(index, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(index, -1);
        break;
      case 'Home':
        e.preventDefault();
        if (enabled[0]) { onValueChange(enabled[0].o.value); refs.current[enabled[0].i]?.focus(); }
        break;
      case 'End': {
        e.preventDefault();
        const last = enabled[enabled.length - 1];
        if (last) { onValueChange(last.o.value); refs.current[last.i]?.focus(); }
        break;
      }
    }
  }

  // Roving tabindex: one stop for the whole group. With nothing selected the
  // first enabled bubble takes the stop, so the group is always reachable.
  const tabbableIndex =
    options.findIndex((o) => o.value === value) >= 0
      ? options.findIndex((o) => o.value === value)
      : (enabled[0]?.i ?? 0);

  return (
    <div role="radiogroup" className={cn('flex flex-wrap gap-2', className)} {...aria}>
      {options.map((opt, i) => {
        const selected = opt.value === value;
        const isDisabled = disabled || opt.disabled;
        return (
          <button
            key={opt.value}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={isDisabled}
            tabIndex={i === tabbableIndex ? 0 : -1}
            onClick={() => onValueChange(opt.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'inline-flex min-h-[44px] flex-col items-start justify-center rounded-lg border px-4 py-2 text-left text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'border-primary bg-primary/5 font-medium text-foreground'
                : 'border-border text-foreground hover:border-primary/40 hover:bg-muted/40'
            )}
          >
            <span>{opt.label}</span>
            {opt.hint && (
              <span className="text-xs font-normal text-muted-foreground">{opt.hint}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

BubbleGroup.displayName = 'BubbleGroup';
